import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaBuilding, FaBox, FaChartBar, FaUsers, FaDollarSign, FaRedo } from 'react-icons/fa';
import SuperAdminSidebar from '../../components/SuperAdmin/SuperAdminSidebar';
import { API_ENDPOINTS } from '../../config/api';
import '../../styles/Dashboard.css';
import '../../styles/SuperAdmin/SuperAdminDashboard.css';

const SuperAdminDashboard = () => {
  const navigate = useNavigate();
  const user = JSON.parse(sessionStorage.getItem('user') || '{}');
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      const token = sessionStorage.getItem('token');
      const authHeaders = {
        Authorization: token ? `Bearer ${token}` : ''
      };
      
      // Fetch all required data
      const [companiesRes, plansRes, subscriptionsRes] = await Promise.all([
        fetch(API_ENDPOINTS.COMPANIES, { headers: authHeaders }),
        fetch(API_ENDPOINTS.PLANS, { headers: authHeaders }),
        fetch(API_ENDPOINTS.SUBSCRIPTIONS, { headers: authHeaders }),
      ]);

      if (!companiesRes.ok || !plansRes.ok || !subscriptionsRes.ok) {
        throw new Error('Failed to fetch dashboard data');
      }

      const companies = await companiesRes.json();
      const plans = await plansRes.json();
      const subscriptions = await subscriptionsRes.json();

      const getCompanyKey = (item) => {
        const byId = item?.companyId?._id || item?.companyId || item?._id;
        if (byId) return String(byId).trim();
        const byName = item?.companyId?.name || item?.company || item?.name;
        return String(byName || '').trim().toLowerCase();
      };

      const latestSubscriptionsByCompany = new Map();
      (Array.isArray(subscriptions) ? subscriptions : []).forEach((subscription) => {
        const key = getCompanyKey(subscription);
        if (!key) return;

        const existing = latestSubscriptionsByCompany.get(key);
        const currentDate = new Date(subscription.renewalDate || subscription.updatedAt || subscription.createdAt || 0).getTime();
        const existingDate = existing
          ? new Date(existing.renewalDate || existing.updatedAt || existing.createdAt || 0).getTime()
          : -1;

        if (!existing || currentDate >= existingDate) {
          latestSubscriptionsByCompany.set(key, subscription);
        }
      });

      const subscribedKeys = new Set(
        (Array.isArray(subscriptions) ? subscriptions : [])
          .map((subscription) => getCompanyKey(subscription))
          .filter(Boolean)
      );

      const legacyActiveCompanies = (Array.isArray(companies) ? companies : []).filter((company) => {
        const key = getCompanyKey(company);
        const hasSubscriptionRecord = key ? subscribedKeys.has(key) : false;
        const hasPlanConfigured = Boolean(company?.plan || company?.planId);
        const isCompanyActive = String(company?.status || '').toLowerCase() === 'active';
        return !hasSubscriptionRecord && hasPlanConfigured && isCompanyActive;
      });

      const latestSubscriptions = Array.from(latestSubscriptionsByCompany.values());
      const activeSubscriptionRows = latestSubscriptions.filter((s) => s.status === 'active');

      // Calculate dashboard metrics
      const activeSubscriptions = activeSubscriptionRows.length + legacyActiveCompanies.length;
      const nearToExpireSubscriptions = activeSubscriptionRows.filter((s) => {
        const days = Number(s?.daysRemaining ?? 0);
        return s?.status === 'active' && days > 0 && days <= 30;
      }).length + legacyActiveCompanies.filter((company) => {
        const days = Number(company?.daysRemaining ?? 0);
        return days > 0 && days <= 30;
      }).length;
      const expiredSubscriptions = latestSubscriptions.filter((s) => {
        const days = Number(s?.daysRemaining ?? 0);
        return s?.status === 'expired' || days <= 0;
      }).length;
      const renewalSubscriptions = activeSubscriptionRows.filter((s) => {
        const days = Number(s?.daysRemaining ?? 0);
        return s?.status === 'active' && (Boolean(s?.upcomingRenewal) || (days > 0 && days <= 30));
      }).length + legacyActiveCompanies.filter((company) => {
        const days = Number(company?.daysRemaining ?? 0);
        return days > 0 && days <= 30;
      }).length;

      const totalRevenue = activeSubscriptionRows
        .reduce((sum, s) => sum + (s.price || 0), 0);

      const legacyRevenue = legacyActiveCompanies.reduce((sum, company) => {
        const companyPrice = Number(company?.price || company?.planId?.price || 0);
        return sum + (Number.isFinite(companyPrice) ? companyPrice : 0);
      }, 0);

      const mergedPlanSources = [
        ...activeSubscriptionRows.map((s) => ({ plan: s.plan, price: s.price })),
        ...legacyActiveCompanies.map((company) => ({
          plan: company?.planId?.name || company?.plan || 'Starter',
          price: company?.price || company?.planId?.price || 0
        }))
      ];

      const planStats = plans.map(plan => {
        const planSubs = mergedPlanSources.filter((s) => String(s?.plan || '').trim() === String(plan?.name || '').trim());
        return {
          name: plan.name,
          subscriptions: planSubs.length,
          revenue: planSubs.reduce((sum, s) => sum + (s.price || 0), 0),
        };
      });

      setDashboardData({
        totalCompanies: companies.length,
        totalPlans: plans.length,
        totalSubscriptions: latestSubscriptions.length + legacyActiveCompanies.length,
        monthlyRevenue: totalRevenue + legacyRevenue,
        activeSubscriptions,
        renewalSubscriptions,
        nearToExpireSubscriptions,
        expiredSubscriptions,
        pendingPlans: 0,
        recentCompanies: companies.slice(0, 3),
        planStats,
      });
    } catch (error) {
   
      // Set dummy data on error
      setDashboardData({
        totalCompanies: 0,
        totalPlans: 0,
        totalSubscriptions: 0,
        monthlyRevenue: 0,
        activeSubscriptions: 0,
        renewalSubscriptions: 0,
        nearToExpireSubscriptions: 0,
        expiredSubscriptions: 0,
        pendingPlans: 0,
        recentCompanies: [],
        planStats: [],
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Check if user is Super Admin
    if (user.role !== 'super_admin') {
      navigate('/');
      return;
    }
    
    fetchDashboardData();
  }, [user.role, navigate, fetchDashboardData]);

  if (loading) {
    return (
      <div className="dashboard-wrapper">
        <SuperAdminSidebar user={user} />
        <div className="dashboard-main">
          <div className="loading-spinner">Loading dashboard...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-wrapper">
      <SuperAdminSidebar user={user} />
      
      <div className="dashboard-main">
        <div className="dashboard-header">
          <h1>Dashboard Overview</h1>
          <p>Welcome back, {user.name}! Here's your system status.</p>
        </div>

        <div className="subscription-summary-grid">
          <button
            type="button"
            onClick={() => navigate('/super-admin/subscriptions?tab=active')}
            className="summary-pill active"
            title="Open Active Subscribers tab"
          >
            Active Subscribers: {dashboardData?.activeSubscriptions || 0}
          </button>
          <button
            type="button"
            onClick={() => navigate('/super-admin/subscriptions?tab=near-expire')}
            className="summary-pill near-expire"
            title="Open Near to Expire tab"
          >
            Near to Expire: {dashboardData?.nearToExpireSubscriptions || 0}
          </button>
          <button
            type="button"
            onClick={() => navigate('/super-admin/subscriptions?tab=expired')}
            className="summary-pill expired"
            title="Open Expired Plans tab"
          >
            Expired Plans: {dashboardData?.expiredSubscriptions || 0}
          </button>
        </div>

        {/* Key Metrics */}
        <div className="metrics-grid superadmin-metrics-grid">
          <div className="metric-card">
            <div className="metric-icon companies"><FaBuilding /></div>
            <div className="metric-content">
              <h3>{dashboardData?.totalCompanies}</h3>
              <p>Total Companies</p>
            </div>
          </div>

          <div className="metric-card">
            <div className="metric-icon plans"><FaBox /></div>
            <div className="metric-content">
              <h3>{dashboardData?.totalPlans}</h3>
              <p>Active Plans</p>
            </div>
          </div>

          <div className="metric-card">
            <div className="metric-icon subscriptions"><FaUsers /></div>
            <div className="metric-content">
              <h3>{dashboardData?.activeSubscriptions}</h3>
              <p>Active Subscriptions</p>
            </div>
          </div>

          <div className="metric-card">
            <div className="metric-icon renewals"><FaRedo /></div>
            <div className="metric-content">
              <h3>{dashboardData?.renewalSubscriptions}</h3>
              <p>Renewals</p>
            </div>
          </div>

          <div className="metric-card">
            <div className="metric-icon revenue"><FaDollarSign /></div>
            <div className="metric-content">
              <h3>${(dashboardData?.monthlyRevenue / 1000).toFixed(0)}K</h3>
              <p>Monthly Revenue</p>
            </div>
          </div>
        </div>

        {/* Charts Section */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '20px', marginBottom: '30px' }}>
          <div style={{ background: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: '0', fontSize: '18px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <FaChartBar /> Plans Performance
              </h2>
              <a href="/super-admin/plans" style={{ color: '#0369a1', textDecoration: 'none', fontSize: '12px' }}>View All →</a>
            </div>
            <div>
              {dashboardData?.planStats.map((plan) => (
                <div key={plan.name} style={{ marginBottom: '15px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                    <span style={{ fontWeight: '500' }}>{plan.name}</span>
                    <span style={{ color: '#666', fontSize: '12px' }}>{plan.subscriptions} subs</span>
                  </div>
                  <div style={{ background: '#e5e7eb', height: '8px', borderRadius: '4px', overflow: 'hidden' }}>
                    <div style={{ background: '#0369a1', height: '100%', width: `${(plan.subscriptions / 20) * 100}%` }}></div>
                  </div>
                  <div style={{ fontSize: '12px', color: '#666', marginTop: '3px' }}>${plan.revenue.toLocaleString()}</div>
                </div>
              ))}
            </div>
          </div>

          <div style={{ background: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: '0', fontSize: '18px', fontWeight: '600' }}>Recent Companies</h2>
              <a href="/super-admin/companies" style={{ color: '#0369a1', textDecoration: 'none', fontSize: '12px' }}>View All →</a>
            </div>
            <div>
              {dashboardData?.recentCompanies.map((company) => (
                <div key={company._id || company.id} style={{ padding: '12px 0', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <h4 style={{ margin: '0 0 4px 0', fontSize: '14px', fontWeight: '600' }}>{company.name}</h4>
                    <p style={{ margin: '0', fontSize: '12px', color: '#666' }}>{company.plan} • {company.users} users</p>
                  </div>
                  <span style={{ background: '#d1fae5', color: '#065f46', padding: '4px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '600' }}>
                    {company.status.toUpperCase()}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div style={{ background: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
          <h2 style={{ margin: '0 0 15px 0', fontSize: '18px', fontWeight: '600' }}>Quick Actions</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px' }}>
            <button 
              onClick={() => navigate('/super-admin/plans')}
              style={{ padding: '12px', background: '#0369a1', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '500', fontSize: '13px' }}>
              Create Plan
            </button>
            <button 
              onClick={() => navigate('/super-admin/companies')}
              style={{ padding: '12px', background: '#7c3aed', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '500', fontSize: '13px' }}>
              View Companies
            </button>
            <button 
              onClick={() => navigate('/super-admin/subscriptions')}
              style={{ padding: '12px', background: '#059669', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '500', fontSize: '13px' }}>
              Manage Subscriptions
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminDashboard;


